/**
 * Data Dictionary Panel
 *
 * Inline UI for managing column-level business metadata.
 * Pre-populates column names from DQ profiling; user fills in
 * descriptions, business rules, and optional detail fields.
 *
 * Primary entry paths (in order):
 * 1. Inline editing — click cells to type
 * 2. Clipboard paste — copy from Excel/Sheets → paste into panel
 * 3. CSV/JSON upload — secondary, future T8
 *
 * Reference: Data_Dictionary_Upload_Design.md
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { parseClipboardText } from './useClipboardPaste';

// ============================================================================
// TYPES
// ============================================================================

interface DictionaryEntry {
  id?: string;
  journey_id?: string;
  column_name: string;
  description: string;
  business_rule?: string;
  expected_format?: string;
  joins_to?: string;
  owner?: string;
  sensitivity?: 'none' | 'pii' | 'phi' | 'confidential';
  source?: string;
  created_at?: string;
  updated_at?: string;
}

interface ProfileColumn {
  name: string;
  type: string;
}

interface DictionaryPanelProps {
  journeyId: string;
  /** Columns from DQ profiling (pre-populates the table) */
  profileColumns?: ProfileColumn[];
  /** Start expanded instead of collapsed (default: false) */
  initiallyExpanded?: boolean;
}

type EditingCell = {
  column: string;
  field: 'description' | 'business_rule';
} | null;

// ============================================================================
// HELPERS
// ============================================================================

const SOURCE_BADGES: Record<string, { label: string; className: string }> = {
  manual: { label: 'Manual', className: 'badge-manual' },
  clipboard: { label: 'Pasted', className: 'badge-clipboard' },
  databricks: { label: 'Databricks', className: 'badge-integration' },
  snowflake: { label: 'Snowflake', className: 'badge-integration' },
  collibra: { label: 'Collibra', className: 'badge-integration' },
  csv_upload: { label: 'CSV', className: 'badge-upload' },
  json_upload: { label: 'JSON', className: 'badge-upload' },
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const DictionaryPanel: React.FC<DictionaryPanelProps> = ({
  journeyId,
  profileColumns = [],
  initiallyExpanded = false,
}) => {
  // State
  const [entries, setEntries] = useState<DictionaryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(!initiallyExpanded);
  const [editingCell, setEditingCell] = useState<EditingCell>(null);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [pasteMessage, setPasteMessage] = useState<string | null>(null);
  const [draftValues, setDraftValues] = useState<Record<string, Partial<DictionaryEntry>>>({});
  const editInputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Count how many columns have definitions
  const definedCount = entries.filter(e => e.description && e.description.trim().length > 0).length;
  const totalColumns = Math.max(profileColumns.length, entries.length);

  // ============================================================================
  // DATA LOADING
  // ============================================================================

  const fetchDictionary = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`/api/contextual-validity/journeys/${journeyId}/dictionary`);
      if (response.ok) {
        const data = await response.json();
        const serverEntries: DictionaryEntry[] = data.entries || [];

        // Merge with profile columns: every profiled column gets a row
        const merged = mergeWithProfile(serverEntries, profileColumns);
        setEntries(merged);
      } else if (response.status === 404) {
        // No dictionary yet — show blank rows from profile
        setEntries(profileColumns.map(col => ({
          column_name: col.name,
          description: '',
          source: 'manual',
        })));
      } else {
        throw new Error(`Failed to load dictionary: ${response.statusText}`);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load dictionary');
    } finally {
      setLoading(false);
    }
  }, [journeyId, profileColumns]);

  useEffect(() => {
    if (journeyId) {
      fetchDictionary();
    }
  }, [journeyId, fetchDictionary]);

  // ============================================================================
  // MERGE LOGIC
  // ============================================================================

  function mergeWithProfile(
    serverEntries: DictionaryEntry[],
    columns: ProfileColumn[]
  ): DictionaryEntry[] {
    const entryMap = new Map<string, DictionaryEntry>(
      serverEntries.map(e => [e.column_name.toLowerCase(), e])
    );

    // Start with profiled columns (preserve order)
    const merged: DictionaryEntry[] = columns.map(col => {
      const existing = entryMap.get(col.name.toLowerCase());
      if (existing) {
        entryMap.delete(col.name.toLowerCase());
        return existing;
      }
      return {
        column_name: col.name,
        description: '',
        source: 'manual',
      };
    });

    // Append any server entries not in the profile (from integrations, etc.)
    for (const entry of entryMap.values()) {
      merged.push(entry);
    }

    return merged;
  }

  // ============================================================================
  // SAVE HANDLER
  // ============================================================================

  const handleSave = async (entry: DictionaryEntry) => {
    try {
      setSaving(true);
      setError(null);

      const entryPayload = {
        column_name: entry.column_name,
        description: entry.description,
        business_rule: entry.business_rule || undefined,
        expected_format: entry.expected_format || undefined,
        joins_to: entry.joins_to || undefined,
        owner: entry.owner || undefined,
        sensitivity: entry.sensitivity || undefined,
      };

      const response = await fetch(
        `/api/contextual-validity/journeys/${journeyId}/dictionary`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            entries: [entryPayload],
            source: entry.source || 'manual',
          }),
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error?.message || `Save failed: ${response.statusText}`);
      }

      const data = await response.json();
      // Update local entries from server response
      if (data.dictionary && Array.isArray(data.dictionary)) {
        const serverEntry = data.dictionary.find(
          (e: DictionaryEntry) => e.column_name.toLowerCase() === entry.column_name.toLowerCase()
        );
        if (serverEntry) {
          setEntries(prev =>
            prev.map(e =>
              e.column_name.toLowerCase() === entry.column_name.toLowerCase()
                ? serverEntry
                : e
            )
          );
        }
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ============================================================================
  // INLINE EDITING
  // ============================================================================

  const startEditing = (column: string, field: 'description' | 'business_rule') => {
    setEditingCell({ column, field });
    // Focus the input after render
    setTimeout(() => editInputRef.current?.focus(), 0);
  };

  const commitEdit = (column: string, field: string, value: string) => {
    setEditingCell(null);

    const entry = entries.find(e => e.column_name === column);
    if (!entry) return;

    const updated = { ...entry, [field]: value, source: entry.source || 'manual' };

    // Update local state immediately
    setEntries(prev =>
      prev.map(e => (e.column_name === column ? updated : e))
    );

    // Auto-save if description is non-empty
    if (updated.description && updated.description.trim().length > 0) {
      handleSave(updated);
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLTextAreaElement>,
    column: string,
    field: string,
    value: string
  ) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      commitEdit(column, field, value);
    }
    if (e.key === 'Escape') {
      setEditingCell(null);
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      commitEdit(column, field, value);
      // Move to the next field
      if (field === 'description') {
        startEditing(column, 'business_rule');
      }
    }
  };

  // ============================================================================
  // EXPANDED ROW FIELDS
  // ============================================================================

  const handleDetailChange = (column: string, field: string, value: string) => {
    setDraftValues(prev => ({
      ...prev,
      [column]: { ...prev[column], [field]: value },
    }));
  };

  const saveDetailFields = (column: string) => {
    const entry = entries.find(e => e.column_name === column);
    const draft = draftValues[column];
    if (!entry || !draft) return;

    const updated = { ...entry, ...draft, source: entry.source || 'manual' };
    setEntries(prev =>
      prev.map(e => (e.column_name === column ? updated : e))
    );
    setDraftValues(prev => {
      const next = { ...prev };
      delete next[column];
      return next;
    });

    if (updated.description && updated.description.trim().length > 0) {
      handleSave(updated);
    }
  };

  // ============================================================================
  // CLIPBOARD PASTE
  // ============================================================================

  const handlePaste = async () => {
    try {
      setPasteMessage(null);
      const text = await navigator.clipboard.readText();
      if (!text || !text.trim()) {
        setPasteMessage('Clipboard is empty. Copy rows from your spreadsheet first.');
        return;
      }

      const parsed = parseClipboardText(text);
      if (parsed.length === 0) {
        setPasteMessage(
          'Could not parse clipboard data. Ensure your spreadsheet has headers: column_name, description'
        );
        return;
      }

      // Match against profiled columns
      const profileNames = new Set(profileColumns.map(c => c.name.toLowerCase()));
      let matched = 0;
      let unmatched = 0;

      const updatedEntries = [...entries];
      for (const pasted of parsed) {
        if (!pasted.column_name) continue;
        const idx = updatedEntries.findIndex(
          e => e.column_name.toLowerCase() === pasted.column_name!.toLowerCase()
        );
        if (idx >= 0) {
          updatedEntries[idx] = {
            ...updatedEntries[idx],
            description: pasted.description || updatedEntries[idx].description,
            business_rule: pasted.business_rule || updatedEntries[idx].business_rule,
            expected_format: pasted.expected_format || updatedEntries[idx].expected_format,
            joins_to: pasted.joins_to || updatedEntries[idx].joins_to,
            owner: pasted.owner || updatedEntries[idx].owner,
            sensitivity: pasted.sensitivity || updatedEntries[idx].sensitivity,
            source: 'clipboard',
          };
          matched++;
        } else if (profileNames.size === 0 || !profileNames.has(pasted.column_name.toLowerCase())) {
          // Unmatched column — add it
          updatedEntries.push({
            column_name: pasted.column_name,
            description: pasted.description || '',
            business_rule: pasted.business_rule,
            expected_format: pasted.expected_format,
            joins_to: pasted.joins_to,
            owner: pasted.owner,
            sensitivity: pasted.sensitivity,
            source: 'clipboard',
          });
          unmatched++;
        }
      }

      setEntries(updatedEntries);
      setPasteMessage(
        `Pasted ${parsed.length} entries. ${matched} matched profiled columns${unmatched > 0 ? `, ${unmatched} new columns added` : ''}.`
      );

      // Bulk save all pasted entries in a single request
      const toSave = updatedEntries.filter(
        e => e.source === 'clipboard' && e.description && e.description.trim().length > 0
      );
      if (toSave.length > 0) {
        try {
          setSaving(true);
          const response = await fetch(
            `/api/contextual-validity/journeys/${journeyId}/dictionary`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                entries: toSave.map(e => ({
                  column_name: e.column_name,
                  description: e.description,
                  business_rule: e.business_rule || undefined,
                  expected_format: e.expected_format || undefined,
                  joins_to: e.joins_to || undefined,
                  owner: e.owner || undefined,
                  sensitivity: e.sensitivity || undefined,
                })),
                source: 'clipboard',
              }),
            }
          );
          if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            throw new Error(data.error?.message || `Bulk save failed: ${response.statusText}`);
          }
          const data = await response.json();
          if (data.dictionary && Array.isArray(data.dictionary)) {
            const merged = mergeWithProfile(data.dictionary, profileColumns);
            setEntries(merged);
          }
        } finally {
          setSaving(false);
        }
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Clipboard access failed';
      if (msg.includes('permission') || msg.includes('denied')) {
        setPasteMessage('Clipboard access denied. Please allow clipboard permission and try again.');
      } else {
        setPasteMessage(`Paste failed: ${msg}`);
      }
    }
  };

  // ============================================================================
  // FILE UPLOAD
  // ============================================================================

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset the input so the same file can be re-selected
    e.target.value = '';

    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!['csv', 'json', 'xlsx'].includes(ext || '')) {
      setPasteMessage('Unsupported file type. Please upload a .xlsx, .csv, or .json file.');
      return;
    }

    try {
      setSaving(true);
      setPasteMessage(null);
      setError(null);

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(
        `/api/contextual-validity/journeys/${journeyId}/dictionary/upload`,
        { method: 'POST', body: formData }
      );

      const data = await response.json();

      if (!response.ok) {
        const errMsg = data.error?.message || `Upload failed: ${response.statusText}`;
        setError(errMsg);
        return;
      }

      // Update entries from server response
      if (data.dictionary && Array.isArray(data.dictionary)) {
        const merged = mergeWithProfile(data.dictionary, profileColumns);
        setEntries(merged);
      }

      setPasteMessage(
        `Uploaded "${file.name}": ${data.entries_processed} entries processed (${data.inserted} new, ${data.updated} updated).`
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'File upload failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadTemplate = () => {
    window.open('/api/contextual-validity/dictionary/template', '_blank');
  };

  // ============================================================================
  // DELETE
  // ============================================================================

  const handleDeleteEntry = async (columnName: string) => {
    try {
      const response = await fetch(
        `/api/contextual-validity/journeys/${journeyId}/dictionary/${encodeURIComponent(columnName)}`,
        { method: 'DELETE' }
      );

      if (response.ok) {
        // Reset entry to blank (keep the row since it's a profiled column)
        setEntries(prev =>
          prev.map(e =>
            e.column_name.toLowerCase() === columnName.toLowerCase()
              ? { column_name: e.column_name, description: '', source: 'manual' }
              : e
          )
        );
      } else {
        setError(`Couldn't clear "${columnName}" (server returned ${response.status}) — the entry is unchanged.`);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  const getSourceBadge = (source?: string) => {
    if (!source) return null;
    const badge = SOURCE_BADGES[source];
    if (!badge) return null;
    return <span className={`dict-source-badge ${badge.className}`}>{badge.label}</span>;
  };

  return (
    <div className="dictionary-panel">
      {/* Header */}
      <button
        className="dictionary-panel-header"
        onClick={() => setCollapsed(!collapsed)}
        aria-expanded={!collapsed}
      >
        <span className="dictionary-panel-title">
          <span className="dictionary-panel-icon">{collapsed ? '\u25B6' : '\u25BC'}</span>
          {' '}Data Dictionary
          {totalColumns > 0 && (
            <span className="dictionary-count-badge">
              {definedCount}/{totalColumns} columns
            </span>
          )}
        </span>
        <span className="dictionary-panel-subtitle">
          Add descriptions and business rules to improve analysis accuracy
        </span>
      </button>

      {/* Collapsible content */}
      {!collapsed && (
        <div className="dictionary-panel-body">
          {/* Error */}
          {error && (
            <div className="dictionary-error" role="alert">
              {error}
              <button onClick={() => setError(null)} className="dictionary-error-dismiss">
                Dismiss
              </button>
            </div>
          )}

          {/* Paste message */}
          {pasteMessage && (
            <div className="dictionary-paste-message" role="status">
              {pasteMessage}
              <button onClick={() => setPasteMessage(null)} className="dictionary-paste-dismiss">
                Dismiss
              </button>
            </div>
          )}

          {/* Toolbar */}
          <div className="dictionary-toolbar">
            <button
              className="dictionary-btn dictionary-btn-template"
              onClick={handleDownloadTemplate}
              disabled={saving}
              title="Download an Excel template with instructions and example data"
            >
              Download Template
            </button>
            <button
              className="dictionary-btn dictionary-btn-upload"
              onClick={() => fileInputRef.current?.click()}
              disabled={saving}
              title="Upload a filled-in Excel (.xlsx), CSV, or JSON file"
            >
              Upload File
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.csv,.json"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
            <button
              className="dictionary-btn dictionary-btn-paste"
              onClick={handlePaste}
              disabled={saving}
              title="Paste tab-separated data from Excel or Google Sheets"
            >
              Paste from Spreadsheet
            </button>
          </div>

          {/* Loading state */}
          {loading && (
            <div className="dictionary-loading">Loading dictionary...</div>
          )}

          {/* Table */}
          {!loading && (
            <div className="dictionary-table-wrapper">
              <table className="dictionary-table">
                <thead>
                  <tr>
                    <th className="dict-col-name">Column</th>
                    <th className="dict-col-desc">Description</th>
                    <th className="dict-col-rule">Business Rule</th>
                    <th className="dict-col-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => {
                    const colKey = entry.column_name;
                    const profileCol = profileColumns.find(
                      c => c.name.toLowerCase() === colKey.toLowerCase()
                    );
                    const isExpanded = expandedRow === colKey;
                    const hasDraft = !!draftValues[colKey];

                    return (
                      <React.Fragment key={colKey}>
                        {/* Main row */}
                        <tr
                          className={`dictionary-row ${entry.description ? 'has-definition' : 'no-definition'}`}
                        >
                          {/* Column name (read-only) */}
                          <td className="dict-col-name">
                            <button
                              className="dict-expand-btn"
                              onClick={() => setExpandedRow(isExpanded ? null : colKey)}
                              title={isExpanded ? 'Collapse details' : 'Expand details'}
                              aria-label={isExpanded ? `Collapse details for ${colKey}` : `Expand details for ${colKey}`}
                              aria-expanded={isExpanded}
                            >
                              {isExpanded ? '\u25BC' : '\u25B6'}
                            </button>
                            <span className="dict-col-name-text">{colKey}</span>
                            {profileCol && (
                              <span className="dict-col-type">({profileCol.type})</span>
                            )}
                            {getSourceBadge(entry.source)}
                          </td>

                          {/* Description (editable) */}
                          <td
                            className="dict-col-desc dict-editable"
                            onClick={() => {
                              if (!editingCell || editingCell.column !== colKey || editingCell.field !== 'description') {
                                startEditing(colKey, 'description');
                              }
                            }}
                          >
                            {editingCell?.column === colKey && editingCell?.field === 'description' ? (
                              <textarea
                                ref={editInputRef}
                                className="dict-edit-input"
                                defaultValue={entry.description || ''}
                                onBlur={(e) => commitEdit(colKey, 'description', e.target.value)}
                                onKeyDown={(e) =>
                                  handleKeyDown(e, colKey, 'description', e.currentTarget.value)
                                }
                                rows={2}
                              />
                            ) : (
                              <span className={entry.description ? '' : 'dict-placeholder'}>
                                {entry.description || 'Click to enter description'}
                              </span>
                            )}
                          </td>

                          {/* Business Rule (editable) */}
                          <td
                            className="dict-col-rule dict-editable"
                            onClick={() => {
                              if (!editingCell || editingCell.column !== colKey || editingCell.field !== 'business_rule') {
                                startEditing(colKey, 'business_rule');
                              }
                            }}
                          >
                            {editingCell?.column === colKey && editingCell?.field === 'business_rule' ? (
                              <textarea
                                ref={editInputRef}
                                className="dict-edit-input"
                                defaultValue={entry.business_rule || ''}
                                onBlur={(e) => commitEdit(colKey, 'business_rule', e.target.value)}
                                onKeyDown={(e) =>
                                  handleKeyDown(e, colKey, 'business_rule', e.currentTarget.value)
                                }
                                rows={2}
                              />
                            ) : (
                              <span className={entry.business_rule ? '' : 'dict-placeholder'}>
                                {entry.business_rule || 'Click to enter rule'}
                              </span>
                            )}
                          </td>

                          {/* Actions */}
                          <td className="dict-col-actions">
                            {entry.description && entry.description.trim().length > 0 && (
                              <button
                                className="dict-delete-btn"
                                onClick={() => handleDeleteEntry(colKey)}
                                title="Clear this entry"
                              >
                                Clear
                              </button>
                            )}
                          </td>
                        </tr>

                        {/* Expanded detail row */}
                        {isExpanded && (
                          <tr className="dictionary-detail-row">
                            <td colSpan={4}>
                              <div className="dict-detail-fields">
                                <div className="dict-detail-field">
                                  <label htmlFor={`dict-format-${colKey}`}>Expected Format</label>
                                  <input
                                    id={`dict-format-${colKey}`}
                                    type="text"
                                    defaultValue={entry.expected_format || ''}
                                    onChange={(e) =>
                                      handleDetailChange(colKey, 'expected_format', e.target.value)
                                    }
                                    placeholder="e.g. ISO-8601, 5-digit numeric"
                                  />
                                </div>
                                <div className="dict-detail-field">
                                  <label htmlFor={`dict-joins-${colKey}`}>Joins To</label>
                                  <input
                                    id={`dict-joins-${colKey}`}
                                    type="text"
                                    defaultValue={entry.joins_to || ''}
                                    onChange={(e) =>
                                      handleDetailChange(colKey, 'joins_to', e.target.value)
                                    }
                                    placeholder="e.g. other_table.column_name"
                                  />
                                </div>
                                <div className="dict-detail-field">
                                  <label htmlFor={`dict-owner-${colKey}`}>Owner</label>
                                  <input
                                    id={`dict-owner-${colKey}`}
                                    type="text"
                                    defaultValue={entry.owner || ''}
                                    onChange={(e) =>
                                      handleDetailChange(colKey, 'owner', e.target.value)
                                    }
                                    placeholder="e.g. Data Engineering"
                                  />
                                </div>
                                <div className="dict-detail-field">
                                  <label htmlFor={`dict-sensitivity-${colKey}`}>Sensitivity</label>
                                  <select
                                    id={`dict-sensitivity-${colKey}`}
                                    defaultValue={entry.sensitivity || 'none'}
                                    onChange={(e) =>
                                      handleDetailChange(colKey, 'sensitivity', e.target.value)
                                    }
                                  >
                                    <option value="none">None</option>
                                    <option value="pii">PII</option>
                                    <option value="phi">PHI</option>
                                    <option value="confidential">Confidential</option>
                                  </select>
                                </div>
                                {hasDraft && (
                                  <button
                                    className="dictionary-btn dictionary-btn-save-detail"
                                    onClick={() => saveDetailFields(colKey)}
                                    disabled={saving}
                                  >
                                    {saving ? 'Saving...' : 'Save Details'}
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Footer status */}
          {!loading && (
            <div className="dictionary-footer">
              <span className="dictionary-status">
                {definedCount} of {totalColumns} columns defined
              </span>
              {saving && <span className="dictionary-saving">Saving...</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
